/* @flow */

import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import Localizer from '../../helpers/localization';
import { WikipediaResultStatusType, type WikipediaResultType, type WikipediaSummary } from '../../helpers/Wikipedia/Types';
import Wikipedia from '../../helpers/Wikipedia/Wikipedia';
import { ignoreIfAborted } from '../../libs/netgemLibrary/helpers/cancellablePromise/promiseHelper';
import ExternalLink from '../link/ExternalLink';
import InfiniteCircleLoader from '../loader/infiniteCircleLoader';
import Tooltip from '../tooltip/Tooltip';
import './Credits.css';
import { CAST_TOOLTIP, GOOGLE_SEARCH_URL } from './helper';

type CreditPropType = {
  +name: string,
};

const renderNotFoundContent = (name: string): React.Node => (
  <div className='tooltipContent castSummary'>
    <div className='summary'>{Localizer.localize('credits.wikipedia.not_found')}</div>

    <ExternalLink textKey='credits.google_search' url={GOOGLE_SEARCH_URL.replace('{searchString}', encodeURIComponent(name))} />
  </div>
);

const renderCastContent = (status: WikipediaResultStatusType, summary: WikipediaSummary, wikipediaUrl: string, wikipediaLicenseUrl: string): React.Node => {
  const { extract, imageUrl, pageUrl, title } = summary;

  const text = status === WikipediaResultStatusType.Success ? extract || '' : Localizer.localize('credits.wikipedia.disambiguation');

  return (
    <div className='tooltipContent castSummary'>
      <div className='detailsAndImage'>
        <div className='details'>
          <div className='name'>{title}</div>
          <div className='summary'>{text}</div>

          <ExternalLink textKey='credits.wikipedia.more' url={pageUrl} />
        </div>

        {imageUrl ? <img alt={Localizer.localize('accessibility.navigation.wikipedia_photo', { name: title })} aria-hidden src={imageUrl} /> : null}
      </div>

      <div className='citation'>
        <div className='copyright'>{Localizer.localize('credits.wikipedia.copyright')}</div>
        <ExternalLink textKey='credits.wikipedia.copyright_name' url={wikipediaUrl} />
        <div className='license'>{Localizer.localize('credits.wikipedia.license')}</div>
        <ExternalLink textKey='credits.wikipedia.license_name' url={wikipediaLicenseUrl} />
      </div>
    </div>
  );
};

const Credit = ({ name }: CreditPropType): React.Node => {
  const wikipediaUrl = useSelector((state) => state.appConfiguration.wikipediaUrl);
  const wikipediaLicenseUrl = useSelector((state) => state.appConfiguration.wikipediaLicenseUrl);

  const abortController = useRef(new AbortController());

  const [open, setOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [wikipediaResult, setWikipediaResult] = useState<WikipediaResultType | null>(null);

  // Only contains a cleanup function
  useEffect(() => () => abortController.current.abort('Component Credit will unmount'), []);

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      setOpen(nextOpen);

      if (!nextOpen) {
        return;
      }

      // If we already have data, don't trigger loading state or fetch again
      if (wikipediaResult !== null) {
        return;
      }

      setIsLoading(true);
      const { signal } = abortController.current;

      Wikipedia.getData(name, signal)
        .then((result) => {
          signal.throwIfAborted();
          setWikipediaResult(result);
          setIsLoading(false);
        })
        .catch((error) => {
          ignoreIfAborted(signal, error);
          setIsLoading(false);
        });
    },
    [name, wikipediaResult],
  );

  const renderContent = useCallback(() => {
    if (isLoading && wikipediaResult === null) {
      // Show loader while data is being retrieved
      return (
        <div aria-busy='true' aria-label={Localizer.localize('accessibility.common.loading')} aria-live='polite' className='tooltipContent castSummary castLoaderWrapper' role='status' tabIndex={-1}>
          <InfiniteCircleLoader />
        </div>
      );
    }

    if (wikipediaResult === null) {
      // Error while loading data
      return null;
    }

    const { status, summary } = wikipediaResult;

    if ((status === WikipediaResultStatusType.Success || status === WikipediaResultStatusType.Disambiguation) && summary) {
      return renderCastContent(status, summary, wikipediaUrl, wikipediaLicenseUrl);
    }

    return renderNotFoundContent(name);
  }, [isLoading, name, wikipediaResult, wikipediaUrl]);

  return (
    <Tooltip
      closeDelay={CAST_TOOLTIP.HideDelay}
      content={renderContent()}
      interactive
      offsetValue={CAST_TOOLTIP.Distance}
      onOpenChange={handleOpenChange}
      open={open}
      openDelay={CAST_TOOLTIP.ShowDelay}
    >
      <button className='cast' type='button'>
        {name}
      </button>
    </Tooltip>
  );
};

export default Credit;
